import {
  Stack,
  Paper,
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from '@mui/material'
import { differenceInMinutes, format, addMinutes } from 'date-fns'
import { i18WithParams as t } from '@shared/locales'
import { useMemo, useState } from 'react'
import { useTheme } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'

import { Types } from 'service-api'
import { formatNumberAsPrice } from '@shared/common'
import { InvoiceStatus } from './invoice_status'
import { CancelInvoiceModal } from './cancel_invoice'
import { canCancelInvoice } from './helper'
import { getInvoiceDates } from 'shared'
import { Link as NavLink, useParams } from 'react-router-dom'

const DEFAULT_DATE_FORMAT = 'MMM d, yyyy'

export type RefundSetting = {
  id: string
  before: number
  after: number
  order: number
  rate: string
  status: string
}

type Props = {
  invoice: Types.CustomInvoiceAdminDto
  order: Types.CustomOrderAdminDto
  isAdmin?: boolean
  canAccessOS?: boolean
  onCancelInvoice: () => void
}

export const InvoiceCard = ({ invoice, order, isAdmin, canAccessOS, onCancelInvoice }: Props) => {
  const theme = useTheme()
  const { accountName: locationName = '' } = useParams()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const cancelButton = useMemo(() => {
    return canCancelInvoice(invoice, order, !!isAdmin) ? (
      <Box>
        <Button variant="outlined" color="error" size="small" onClick={() => setCancelModalOpen(true)}>
          {t('global.label.cancelBooking')}
        </Button>
      </Box>
    ) : null
  }, [invoice, order, isAdmin])

  const invoiceFor = useMemo(() => {
    const item = (invoice.items || []).find((item) => item.offerType === 'PRIMARY')
    const primaryOfferName = item?.offer?.displayName || item?.offer?.locationServiceType?.displayName
    const petNames = (item?.petRelations || []).map(({ name }) => name).join(', ')
    return `${primaryOfferName}${petNames ? ` (${petNames})` : ''}`
  }, [invoice])

  const invoiceServiceDates = useMemo(() => {
    const { startDate, endDate } = getInvoiceDates(invoice)
    if (order.primaryServiceType?.serviceType?.overnight) {
      return `${format(startDate, DEFAULT_DATE_FORMAT)} - ${format(endDate, DEFAULT_DATE_FORMAT)}`
    }
    // eslint-disable-next-line no-extra-boolean-cast
    const timeFormat = !!invoice.period?.[0].startTime ? ' @ h:mm aaaa' : ''
    return format(startDate, `EEE MMM d, yyyy${timeFormat}`)
  }, [invoice, order.primaryServiceType?.serviceType?.overnight])

  const cancelBy = useMemo(() => {
    const refundSettings: RefundSetting[] = order.data?.refundSettings || []
    const { startDate: reservationDate } = getInvoiceDates(invoice)
    const minutesToReservation = differenceInMinutes(reservationDate, new Date())
    const numMinutesCanCancel = refundSettings.reduce((res, setting) => {
      if (minutesToReservation > setting.before) {
        return res ? Math.min(res, setting.before) : setting.before
      }
      return res
    }, 0)
    return numMinutesCanCancel
      ? format(addMinutes(reservationDate, -numMinutesCanCancel), DEFAULT_DATE_FORMAT)
      : t('global.label.N/A')
  }, [invoice, order])

  const cancellationDetails = useMemo(() => {
    const { canceledAt, cancellationReason } = invoice
    const date = format(new Date(canceledAt || 0), DEFAULT_DATE_FORMAT)
    switch (cancellationReason) {
      case 'USER': {
        return t('invoiceCard.cancelByCustomer', { date })
      }
      case 'LOCATION': {
        return t('invoiceCard.cancelByStaff', { date })
      }
      default: {
        return t('invoiceCard.cancelByOther', { date })
      }
    }
  }, [invoice])

  const refundDetails = useMemo(() => {
    return invoice.refunded
      ? t('invoiceCard.refunded', {
          refund: formatNumberAsPrice(invoice.refunded),
          deposit: formatNumberAsPrice(invoice.charged || 0),
        })
      : ''
  }, [invoice.refunded, invoice.charged])

  const tilledLink = useMemo(() => {
    const existingPayment = order.payments?.[0] || {}
    const env = import.meta.env.VITE_TILLED_ENVIRONMENT
    const tilledDomain =
      env === 'prod' ? `https://goose.paymentsonline.io` : `https://goose-${env}.sandbox-paymentsonline.io`
    const linkURL = existingPayment.merchantId
      ? `${tilledDomain}/${existingPayment.merchantId}/payments/${existingPayment.paymentIntentId}`
      : ''
    return isAdmin &&
      existingPayment.merchantId &&
      existingPayment.paymentIntentId &&
      existingPayment.payFacType === 'TILLED' ? (
      <Button variant="outlined" size="small" target="_blank" href={linkURL}>
        {t('invoiceCard.viewPaymentTransaction')}
      </Button>
    ) : null
  }, [order.payments, isAdmin])

  const invoiceOSLink = useMemo(() => {
    if (!isAdmin || !canAccessOS) {
      return null
    }
    return (
      <Button component={NavLink} to={`/v2/${locationName}/invoice/${invoice.id}`} target="_blank" variant="outlined">
        {t('invoiceCard.viewInvoiceInOS')}
      </Button>
    )
  }, [isAdmin, invoice.id, locationName])

  return (
    <>
      <Paper sx={{ p: 2, borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
        <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap">
          <Stack direction="row" spacing={2} alignItems="center">
            <InvoiceStatus invoice={invoice} />
            <Stack>
              <Typography variant="body1">
                {t('invoiceCard.confirmationNum', {
                  num: invoice?.name?.toString() ?? '',
                })}
              </Typography>
              {invoice.invoiceStatus === 'CANCELED' && invoice.canceledAt && (
                <Typography variant="body2">{`${cancellationDetails} ${refundDetails}`}</Typography>
              )}
            </Stack>
          </Stack>
          {cancelButton}
        </Stack>
        <TableContainer sx={{ mt: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('global.label.for')}</TableCell>
                <TableCell>{t('global.label.reservedOn')}</TableCell>
                <TableCell>{t('global.label.cancelBy')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ verticalAlign: 'baseline' }}>
                <TableCell width="40%">
                  <Typography variant="body2">{invoiceFor}</Typography>
                  <Typography variant="body2">{invoiceServiceDates}</Typography>
                </TableCell>
                <TableCell width="30%">{format(new Date(invoice?.createdAt ?? 0), DEFAULT_DATE_FORMAT)}</TableCell>
                <TableCell>{cancelBy}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Stack direction="row" spacing={2} mt={4}>
          {tilledLink}
          {invoiceOSLink}
        </Stack>
      </Paper>
      <CancelInvoiceModal
        invoice={invoice}
        order={order}
        canOverrideRefund={isAdmin}
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onCancelInvoice={onCancelInvoice}
      />
    </>
  )
}
